<template>
  <page-outline 
    :title="$t('common.alerts.alerts_history')"
    :show-filter="true"
    :show-refresh="true"
    :filter-expanded="displayFilter"
    :refresh-disabled="activateSpinner"
    @toggle-filter="toggleFilter"
    @refresh="onRefresh">
    
    <template slot="filterContent">
      <app-panel
        :filters="filterConfig"
        :searchAriaLabel="$t('common.srch') + ' ' + $t('common.alerts.alerts_history')"
        @filter-changed="onFilterChanged"
        @clear-filters="clearFilterFields"
        @apply-filters="onRefresh"
      />
    </template>
    
    <template slot="pageContent">
      <div class="listPage ml-3 mr-3">
        <b-row class="mb-5">
          <!-- {{engineOptions}} -->
          <b-col sm="6" lg="3" cols="12" class="filter-item-action mb-5">
            <label class="filter-item-title text-ellipsis" for="enginekey">
              {{ $t('common.alerts.engine') }}
            </label>
            <select class="filter-item-content" v-model="engineIndex" @change="getAlertsHistory()" id="enginekey"
                    style="max-width:100%;padding:0.5rem;"
                    autocomplete="off">
              <option v-for="(engine, index) in engineOptions" :key="index" :value="index" :disabled="engine.disabled">
                {{ engine.name }}
              </option>
            </select>
          </b-col>
          <b-col>
            <div style="overflow:auto">
              <spinner v-if="activateSpinner"></spinner>
              <b-table responsive
                v-else-if="alertsHistoryList.length"
                class="mt-3 table-striped"
                :items="alertsHistoryList"
                :fields="alertsHistoryFields"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDir"
                @head-clicked="sortTable"
                caption-top
                role="table"
                aria-label="Historique des alertes">
                <template #cell(MetricsKey)="data">
                  <span>{{ $t('common.monitoring.key.' + data.value) }}</span>
                </template>
                <template #cell(ThresholdStatus)="data">
                  <span>{{ $t('common.alerts.threshold_status_value.'+data.value) }}</span>
                </template>
                <template #cell(DateStart)="data">
                  <span>{{ getDate(data.value) }}</span>
                </template>
                <template #cell(DateEnd)="data">
                  <span>{{ getDate(data.value) }}</span>
                </template>
                <template #cell(Criticality)="data">
                  <i v-if="translateCriticalityFromDatabase(data.value) == 'LOW'" class="fa-solid fa-circle color-light-blue" aria-hidden="true"></i>
                  <i v-if="translateCriticalityFromDatabase(data.value) == 'MEDIUM'" class="fa-solid fa-circle color-yellow" aria-hidden="true"></i>
                  <i v-if="translateCriticalityFromDatabase(data.value) == 'HIGH'" class="fa-solid fa-circle color-orange" aria-hidden="true"></i>
                  <i v-if="translateCriticalityFromDatabase(data.value) == 'CRITICAL'" class="fa-solid fa-circle color-red" aria-hidden="true"></i>
                  <span>{{ $t('common.alerts.' + translateCriticalityFromDatabase(data.value)) }}</span>
                </template>
                <template #cell(status)="data">
                  <i v-if="data.value == 0" class="fa-regular fa-circle-check color-lime-green" aria-hidden="true"></i>
                  <i v-if="data.value == 1" class="fa-regular fa-circle-xmark text-danger" aria-hidden="true"></i>
                  <span class="sr-only">{{ data.value == 0 ? $t('common.alerts.status_solved') : $t('common.alerts.status_on_going') }}</span>
                </template>
              </b-table>
              <div v-else class="d-flex justify-content-center">
                <span class="noResult">
                  {{ $t('common.no_result') }}
                </span>
              </div>
            </div>
          </b-col>
        </b-row>
        <b-row class="mt-5">
          <b-col>
            <cm-pagination
              :maxResultsByPage="paginationObject.maxResults"
              :firstIndex="paginationObject.firstIndex"
              :nbResultsTotal="paginationObject.nbResultsTotal"
              :refreshEvent="paginationObject.refresh">
            </cm-pagination>
          </b-col>
        </b-row>
      </div>
    </template>
  </page-outline>
</template>

<script>
import dateTimePicker from '@/components/common/dateTimePicker.vue'
import Pagination from '@/components/common/pagination.vue'
import moment from 'moment-timezone'
import PageOutline from "@/components/common/PageOutline.vue"
import Spinner from "@/components/common/spinner.vue"
import AppButton from '@/components/common/AppButton.vue'
import AppPanel from '@/components/common/AppPanel.vue'
import {errorMessageAlert} from '@/helpers/alert'

export default {
  name: "AlertsPage",
  components: {
    'cm-pagination': Pagination,
    'datetime-picker-custom': dateTimePicker,
    'page-outline': PageOutline,
    'spinner': Spinner,
    'app-button': AppButton,
    'app-panel': AppPanel
  },
  data() {
    return {
      engineIndex: undefined,
      engineOptions: [],
      activateSpinner: false,
      alertsHistoryList: [],
      alertsHistoryFields: [],
      criticality: '',
      dateEnd: '',
      dateStart: '',
      displayFilter: false,
      filterConditions: {},
      pageCounter: 0,
      paginationObject: {},
      sortBy: '',
      sortDir: false,
      status: '',
      timezone: '+00:00',
      filterValues: {
        dateStart: '',
        dateEnd: '',
        criticality: '',
        status: ''
      }
    }
  },
  created() {
    this.timezone = moment.tz.guess()

    let that = this
    this.$engine.engines.forEach((engine, index) => {
      if (that.engineIndex === undefined && engine.common.network.state === 'online') {
        that.engineIndex = index
      }
      that.engineOptions.push({name:engine.common.network.info.name, disabled: engine.common.network.state === 'offline'})
    })
    if (this.filterConditions) {
      if (this.filterConditions.OrderByField) {
        this.sortBy = this.filterConditions.OrderByField
      }
      if (this.filterConditions.OrderByDirection) {
        this.sortDir = this.filterConditions.OrderByDirection === 'DESC'
      }
    }

    this.activateSpinner = true
    this.alertsHistoryFields = [
      { key: 'AlertName', label: this.$t('common.alerts.name'), class: 'text-center', sortable: true },
      { key: 'IdPod', label: this.$t('common.alerts.pod'), class: 'text-center', sortable: true },
      { key: 'MetricsKey', label: this.$t('common.alerts.metrics_key'), class: 'text-center', sortable: true },
      { key: 'ThresholdStatus', label: this.$t('common.alerts.threshold_status'), class: 'text-center', sortable: true },
      { key: 'DateStart', label: this.$t('common.alerts.date_start'), class: 'text-center', sortable: true },
      { key: 'DateEnd', label: this.$t('common.alerts.date_end'), class: 'text-center', sortable: true },
      { key: 'Criticality', label: this.$t('common.alerts.criticality_level'), class: 'text-center', sortable: true },
      { key: 'Status', label: this.$t('common.alerts.status'), class: 'text-center', sortable: true }
    ]

    this.paginationObject['firstIndex'] = 1
    this.paginationObject['maxResults'] = 15
    this.paginationObject['nbResultsTotal'] = 0
    this.$bus.$off('refresh-cache-contacts')

    this.paginationObject['refresh'] = 'direct-inbox-filtered'
    this.$bus.$off('direct-inbox-filtered')
    this.$bus.$on('direct-inbox-filtered', index => {
      this.getAlertsHistory(index)
      that.pageCounter = 0
    })

    this.$f7.initPullToRefresh()
    this.pageCounter = 0

    this.getAlertsHistory()
  },
  computed: {
    filterConfig() {
      const criticalityOptions = [
        { value: 'CRITICAL', labelKey: 'common.alerts.critical' },
        { value: 'HIGH', labelKey: 'common.alerts.high' },
        { value: 'MEDIUM', labelKey: 'common.alerts.medium' },
        { value: 'LOW', labelKey: 'common.alerts.low' }
      ]
      
      const statusOptions = [
        { value: '0', labelKey: 'common.alerts.status_solved' },
        { value: '1', labelKey: 'common.alerts.status_on_going' }
      ]
      
      return [
        {
          id: 'filter-date-start',
          type: 'datetime',
          prop: 'dateStart',
          labelKey: 'common.alerts.date_start',
          icon: 'fa-solid fa-calendar',
          value: this.filterValues.dateStart,
          format: 'DD/MM/YYYY H:i:s',
          maxLength: 19,
          colSm: '6',
          colLg: '3'
        },
        {
          id: 'filter-date-end',
          type: 'datetime',
          prop: 'dateEnd',
          labelKey: 'common.alerts.date_end',
          icon: 'fa-solid fa-calendar',
          value: this.filterValues.dateEnd,
          format: 'DD/MM/YYYY H:i:s',
          maxLength: 19,
          colSm: '6',
          colLg: '3'
        },
        {
          id: 'filter-criticality',
          type: 'select',
          prop: 'criticality',
          labelKey: 'common.alerts.criticality_level',
          icon: 'fa-solid fa-exclamation-triangle',
          value: this.filterValues.criticality,
          defaultOptionKey: 'common.alerts.select_criticality_level_placeholder',
          options: criticalityOptions,
          colSm: '6',
          colLg: '3'
        },
        {
          id: 'filter-status',
          type: 'select',
          prop: 'status',
          labelKey: 'common.alerts.status',
          icon: 'fa-solid fa-chart-line',
          value: this.filterValues.status,
          defaultOptionKey: 'common.alerts.select_status_placeholder',
          options: statusOptions,
          colSm: '6',
          colLg: '3'
        }
      ]
    }
  },
  methods: {
    applyFilter(filterConditions = {}) {
      this.filterConditions = filterConditions

      this.pageCounter = 0
      this.paginationObject['firstIndex'] = 1
      this.paginationObject['nbResultsTotal'] = 0

      this.activateSpinner = true

      if (this.filterConditions.OrderByField) {
        this.sortBy = this.filterConditions.OrderByField
      }
      if (this.filterConditions.OrderByDirection) {
        this.sortDir = this.filterConditions.OrderByDirection === 'DESC'
      }

      this.getAlertsHistory()
    },
    clearFilterFields() {
      this.criticality = ''
      this.dateEnd = ''
      this.dateStart = ''
      this.status = ''
      this.filterValues.dateStart = ''
      this.filterValues.dateEnd = ''
      this.filterValues.criticality = ''
      this.filterValues.status = ''
    },
    onFilterChanged({ prop, value }) {
      this.filterValues[prop] = value
      if (prop === 'dateStart') {
        this.dateStart = value
      } else if (prop === 'dateEnd') {
        this.dateEnd = value
      } else if (prop === 'criticality') {
        this.criticality = value
      } else if (prop === 'status') {
        this.status = value
      }
    },
    toggleFilter() {
      this.displayFilter = !this.displayFilter
    },
    /*colorLineByCriticality(item, type) {
      if (!item || type !== "row") return;
      return;
      switch (this.translateCriticalityFromDatabase(item.Criticality)) {
        case "LOW":
          return "row-criticality-low";
        case "MEDIUM":
          return "row-criticality-medium";
        case "HIGH":
          return "row-criticality-high";
        case "CRITICAL":
          return "row-criticality-critical";
        default:
          return "";
      }
    },*/
    getAlertsHistory(index = 1) {
      this.pageCounter = 0
      this.paginationObject['firstIndex'] = index
      this.paginationObject['nbResultsTotal'] = 0
      let that = this
      let params = {
        Status: this.status,
        Criticality: this.translateCriticalityToDatabase(this.criticality),
        DateStart: this.dateStart ? moment(this.dateStart, 'DD/MM/YYYY HH:mm:ss').tz(this.timezone).format('DD/MM/YYYY HH:mm:ss.SSSZ') : '',
        DateEnd: this.dateEnd ? moment(this.dateEnd, 'DD/MM/YYYY HH:mm:ss').tz(this.timezone).format('DD/MM/YYYY HH:mm:ss.SSSZ') : '',
        maxResults: this.paginationObject.maxResults,
        firstIndex: this.paginationObject.firstIndex
      }
      if (this.filterConditions.OrderByField) {
        params.OrderByField = this.filterConditions.OrderByField
      }
      if (this.filterConditions.OrderByDirection) {
        params.OrderByDirection = this.filterConditions.OrderByDirection
      }
      this.$engine.get(this.engineIndex).alerts.getHistory(params).then((resp) => {
        that.paginationObject.nbResultsTotal = resp.data.nbTotalResults
        that.paginationObject.nbResults = resp.data.AlertsHistoryList.length

        that.alertsHistoryList = resp.data.AlertsHistoryList
        that.$f7.pullToRefreshDone()
        that.activateSpinner = false
      })
      .catch(error => {
        if (error.code === 'ERR_NETWORK') {
          that.engineOptions[this.engineIndex].disabled = true
          for (let i = 0; i < that.engineOptions.length; i++) {
            if (i !== that.engineIndex) {
              that.engineIndex = i
              break
            }
          }
        } else {
          errorMessageAlert(error, that)
          that.okMessageBox(that.$t('common.alerts.alert_configuration_delete_error'))
        }
      })
    },
    getDate (date) {
      if (!date) return '-';

      // Si la date ne contient pas un offset explicite (+00:00, +04:00, etc.) ou 'Z', on suppose qu'elle est en UTC
      const hasTimezone = /([+-]\d{2}:\d{2}|Z)$/.test(date);
      const isoWithTZ = hasTimezone ? date : date + 'Z';

      return moment(isoWithTZ).tz(this.timezone).format('DD/MM/YYYY HH:mm:ss.SSS');
    },
    onRefresh() {
      this.activateSpinner = true
      this.getAlertsHistory()
    },
    sortTable (key, field, event, isFooter) {
      let sortDirection = 'ASC'
      if (key === this.sortBy && this.sortDir === false) {
        sortDirection = 'DESC'
      }

      this.filterConditions.OrderByField = key
      this.filterConditions.OrderByDirection = sortDirection

      this.applyFilter(this.filterConditions, this.paginationObject.firstIndex)
    },
    translateCriticalityFromDatabase(criticality) {
      if (criticality === '0') {
        return "LOW"
      } else if (criticality === '1') {
        return "MEDIUM"
      } else if (criticality === '2') {
        return "HIGH"
      } else if (criticality === '3') {
        return "CRITICAL"
      } else {
        return ''
      }
    },
    translateCriticalityToDatabase(criticality) {
      if (criticality === "LOW") {
        return '0'
      } else if (criticality === "MEDIUM") {
        return '1'
      } else if (criticality === "HIGH") {
        return '2'
      } else if (criticality === "CRITICAL") {
        return '3'
      } else {
        return ''
      }
    },
    translateMetricKey(key) {
        if (key.includes("Connecteur")) {
          return this.$t('common.alerts.' + key.replace(' ', ''))
        } else {
          return key
        }
    }
  }
}
</script>
